import { createFileRoute, Link } from "@tanstack/react-router";
import { Scale, Files, ShieldCheck, ArrowRight, BookOpen, Gavel, FileText, GraduationCap } from "lucide-react";

export const Route = createFileRoute("/education/research/")({
  head: () => ({
    meta: [
      {
        title: "Legal Research Hub | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "AI-assisted legal research for SA students & practitioners: IRAC generator, case briefs, memo templates and citation verifier.",
      },
      { property: "og:title", content: "Legal Research Hub" },
      {
        property: "og:description",
        content:
          "IRAC analyses, case briefs, downloadable research templates and a source-verification guardrail.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/education/research" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/education/research" }],
  }),
  component: ResearchHub,
});

const TOOLS = [
  {
    to: "/education/research/assignment",
    icon: GraduationCap,
    title: "Assignment Assistant",
    body: "Plan, draft or improve a law-school assignment with structured headings, footnote markers, a bibliography and a submission checklist — in OSCOLA, Harvard, APA 7 or SA citation.",
    cta: "Open assignment assistant",
    credits: "1 credit",
  },
  {
    to: "/education/research/irac",
    icon: Scale,
    title: "IRAC Generator",
    body: "Turn facts + a legal question into a labelled Issue / Rule / Application / Conclusion analysis with [REQUIRES VERIFICATION] flags on any uncertain authority.",
    cta: "Open IRAC generator",
    credits: "1 credit",
  },
  {
    to: "/education/research/case-analysis",
    icon: BookOpen,
    title: "Case Analysis Tool",
    body: "Generate a structured case brief — parties, facts, issue, holding, ratio, order — refusing to fabricate when it doesn't know the case.",
    cta: "Open case-analysis tool",
    credits: "1 credit",
  },
  {
    to: "/education/research/judgment-summary",
    icon: Gavel,
    title: "Judgment Summary Generator",
    body: "Paste a judgment; get a structured summary — facts, legal principles engaged, ratio decidendi, order and significance — built only from the text you supplied.",
    cta: "Open judgment summariser",
    credits: "1 credit",
  },
  {
    to: "/education/research/footnotes",
    icon: FileText,
    title: "Footnote Generator",
    body: "Paste a draft passage; we insert numbered footnote markers and produce a matching footnote list in OSCOLA, Harvard, APA 7 or SA citation.",
    cta: "Open footnote generator",
    credits: "1 credit",
  },
  {
    to: "/education/research/sources",
    icon: ShieldCheck,
    title: "Source Verification",
    body: "Paste a list of citations; we check their FORMAT against OSCOLA, Harvard, APA 7 and SA citation rules and flag pieces you must verify at the primary source. We never claim a case 'exists'.",
    cta: "Open source verifier",
    credits: "1 credit",
  },
  {
    to: "/education/research/templates",
    icon: Files,
    title: "Research Templates",
    body: "Editable RTF templates: research memorandum, heads of argument, legal opinion — drop into Word or Google Docs and adapt.",
    cta: "Browse templates",
    credits: "Free",
  },
];

function ResearchHub() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold">
            Education · Legal Research Hub
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            Research smarter — without inventing authority
          </h1>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground">
            Every AI tool here is wired with a verification guardrail. When the model is unsure of a
            citation, year, court, or section number it must flag{" "}
            <span className="font-mono text-navy">[REQUIRES VERIFICATION]</span> instead of guessing.
            Use the templates for the long-form writing.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/education/legal-article-guide"
              className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-medium text-navy hover:bg-gold/90"
            >
              <BookOpen className="size-4" /> Standing guidelines — Legal Article Writing Guide
              <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/education/research/worksheet"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-white hover:bg-navy/90"
            >
              Guided research worksheet
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="container-narrow py-12">
        <div className="grid gap-5 md:grid-cols-2">
          {TOOLS.map((t) => (
            <article key={t.to} className="rounded-lg border border-input bg-card p-6">
              <div className="flex items-start gap-3">
                <span className="rounded-md bg-navy/5 p-2 text-navy">
                  <t.icon className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="font-display text-xl text-navy">{t.title}</h2>
                  <p className="text-[10px] uppercase tracking-widest text-gold">{t.credits}</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{t.body}</p>
              <Link
                to={t.to as any}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline"
              >
                {t.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </article>
          ))}
        </div>

        <aside className="mt-10 rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
          <p className="font-semibold">Important</p>
          <p className="mt-1">
            AI output is a starting point for legal research, never the end. You must independently
            verify every authority, citation, statute reference and legal conclusion against the primary
            source (an official law report, gov.za, or the relevant court's record) before relying on it
            in any assignment, opinion, court document, or advice.
          </p>
        </aside>
      </section>
    </>
  );
}